/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";

const state = {
  presetRecords: [],
  loadingPresetRecord: false,
  loadingPresetRecords: false
};

const actions = {
  async loadPresetRecords({ commit, getters }, dataSetId) {
    if (!dataSetId) {
      console.error("DataSet ID is required");
      return;
    }
    commit("setLoadingPresetRecords", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataRef = collection(dataSetRef, "data");
      const querySnapshot = await getDocs(dataRef);

      const presetRecords = [];
      querySnapshot.forEach(function(docSnapshot) {
        const data = docSnapshot.data();
        presetRecords.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setPresetRecords", presetRecords);
    } catch (error) {
      console.error("Error loading preset records:", error);
      commit("setPresetRecords", []);
    } finally {
      commit("setLoadingPresetRecords", false);
    }
  },

  async getPresetRecords({ commit, getters }, dataSetId) {
    if (!dataSetId) {
      console.error("DataSet ID is required");
      return [];
    }

    commit("setLoadingPresetRecords", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataRef = collection(dataSetRef, "data");
      const querySnapshot = await getDocs(dataRef);

      const presetRecords = [];
      querySnapshot.forEach(function(docSnapshot) {
        const data = docSnapshot.data();
        presetRecords.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return presetRecords;
    } catch (error) {
      console.error("Error getting preset records:", error);
      return [];
    } finally {
      commit("setLoadingPresetRecords", false);
    }
  },

  async getPresetRecordById({ getters }, { dataSetId, recordId }) {
    if (!dataSetId || !recordId) {
      console.error("DataSet ID and Record ID are required");
      return null;
    }
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const recordRef = doc(collection(dataSetRef, "data"), recordId);
      const recordDoc = await getDoc(recordRef);

      if (recordDoc.exists()) {
        const data = recordDoc.data();
        return {
          id: recordDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Preset record with ID ${recordId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting preset record by ID:", error);
      return null;
    }
  },

  async dataSetHasRecords({ getters }, dataSetId) {
    if (!dataSetId) {
      console.error("DataSet ID is required");
      return false;
    }
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataRef = collection(dataSetRef, "data");
      const limitedQuery = query(dataRef, limit(1));
      const snapshot = await getDocs(limitedQuery);

      return !snapshot.empty;
    } catch (error) {
      console.error("Error checking for records:", error);
      return false;
    }
  },

  async recordValidationHistory({ getters }, payload) {
    console.log(
      "🔍 [DEBUG] recordValidationHistory called with payload:",
      payload
    );

    if (!payload || !payload.dataSetId || !payload.recordId) {
      console.error("❌ Payload with dataSetId and recordId is required");
      console.error("❌ Received payload:", payload);
      return false;
    }
    const {
      dataSetId,
      recordId,
      offerId,
      opportunityId,
      entryId,
      memberId,
      fieldId,
      fieldLabel,
      value,
      validationPresetRecordSnapshot
    } = payload;

    console.log("🔍 [DEBUG] Extracted payload data:");
    console.log("🔍 [DEBUG] - dataSetId:", dataSetId);
    console.log("🔍 [DEBUG] - recordId:", recordId);
    console.log("🔍 [DEBUG] - offerId:", offerId);
    console.log("🔍 [DEBUG] - opportunityId:", opportunityId);
    console.log("🔍 [DEBUG] - entryId:", entryId);
    console.log("🔍 [DEBUG] - memberId:", memberId);
    console.log("🔍 [DEBUG] - fieldId:", fieldId);
    console.log("🔍 [DEBUG] - fieldLabel:", fieldLabel);
    console.log("🔍 [DEBUG] - value:", value);

    try {
      console.log(
        "🔍 [DEBUG] Getting programId from getters:",
        getters.programId
      );

      // Reference to the validationHistory subcollection for this record
      const programRef = doc(db(), "programs", getters.programId);
      console.log("🔍 [DEBUG] Program reference created");

      const validationHistoryRef = collection(
        doc(
          collection(programRef, "presetDataSets"),
          dataSetId,
          "data",
          recordId
        ),
        "validationHistory"
      );
      console.log("🔍 [DEBUG] Validation history reference created");
      console.log(
        "🔍 [DEBUG] Validation history path:",
        `programs/${getters.programId}/presetDataSets/${dataSetId}/data/${recordId}/validationHistory`
      );

      // Create the validation history entry with all metadata
      const validationEntry = {
        offerId,
        opportunityId,
        entryId,
        memberId,
        fieldId,
        fieldLabel,
        valueEntered: value,
        validatedAt: serverTimestamp(),
        validationPresetRecordSnapshot,
        // Add reference to the entry for easy lookup
        entryRef: entryId
          ? `programs/${getters.programId}/offers/${offerId}/entries/${entryId}`
          : null
      };

      console.log("🔍 [DEBUG] Validation entry to be saved:", validationEntry);

      // Add the entry to the validationHistory subcollection
      console.log(
        "🔍 [DEBUG] Attempting to add document to validationHistory collection..."
      );
      const docRef = await addDoc(validationHistoryRef, validationEntry);
      console.log("🔍 [DEBUG] Document added successfully with ID:", docRef.id);

      // If we have an entryId, try to add a reference to this validation history document in the entry
      // This is optional and won't fail the validation if permissions are insufficient
      if (entryId && offerId) {
        console.log("🔍 [DEBUG] Adding validation reference to entry...");
        try {
          const entryRef = doc(
            collection(programRef, "offers"),
            offerId,
            "entries",
            entryId
          );

          console.log("🔍 [DEBUG] Checking if entry document exists...");
          // First check if the entry document exists
          const entryDoc = await getDoc(entryRef);
          if (!entryDoc.exists()) {
            console.warn(
              "⚠️ Entry document does not exist yet - validation reference will be skipped:",
              entryId
            );
            return true; // Still return success since validation history was recorded
          }

          console.log(
            "🔍 [DEBUG] Entry document exists, adding validation reference..."
          );
          // Update the entry to include the validation history reference in a subcollection
          // This creates an efficient bidirectional reference
          const validationReferencesRef = collection(
            entryRef,
            "validationReferences"
          );
          const validationRefDoc = await addDoc(validationReferencesRef, {
            validationHistoryPath: `programs/${getters.programId}/presetDataSets/${dataSetId}/data/${recordId}/validationHistory/${docRef.id}`,
            fieldId,
            fieldLabel,
            dataSetId,
            recordId,
            value,
            validatedAt: serverTimestamp()
          });
          console.log(
            "🔍 [DEBUG] Validation reference added with ID:",
            validationRefDoc.id
          );
        } catch (error) {
          // Silently continue if this part fails - the validation history is still recorded
          console.warn(
            "⚠️ Could not create validation reference in entry:",
            error.code,
            error.message
          );
          console.warn("⚠️ Error details:", error);
        }
      } else {
        console.log(
          "🔍 [DEBUG] Skipping validation reference - no entryId or offerId"
        );
      }

      console.log("✅ [DEBUG] recordValidationHistory completed successfully");
      return true;
    } catch (error) {
      console.error("❌ Error recording validation history:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);
      console.error(
        "❌ Error details - dataSetId:",
        dataSetId,
        "recordId:",
        recordId
      );
      return false;
    }
  },

  async getEntryValidationHistory({ getters }, { offerId, entryId }) {
    if (!offerId || !entryId) {
      console.error("OfferId and entryId are required");
      return [];
    }
    try {
      // Reference to the entry's validation references
      const programRef = doc(db(), "programs", getters.programId);
      const validationRefsRef = collection(
        doc(collection(programRef, "offers"), offerId, "entries", entryId),
        "validationReferences"
      );

      // Get all validation references for this entry
      const snapshot = await getDocs(validationRefsRef);

      if (snapshot.empty) {
        return [];
      }

      // Extract validation references
      const validationRefs = [];
      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        validationRefs.push({
          refId: docSnapshot.id,
          ...data,
          validatedAt: data.validatedAt?.toDate() || new Date()
        });
      });

      return validationRefs;
    } catch (error) {
      console.error("Error retrieving entry validation history:", error);
      return [];
    }
  },

  async getFieldValidationHistory({ getters }, { dataSetId, recordId }) {
    if (!dataSetId || !recordId) {
      console.error("DataSetId and recordId are required");
      return [];
    }
    try {
      // Reference to the validation history for this record
      const programRef = doc(db(), "programs", getters.programId);
      const validationHistoryRef = collection(
        doc(
          collection(programRef, "presetDataSets"),
          dataSetId,
          "data",
          recordId
        ),
        "validationHistory"
      );

      // Get all validation history entries ordered by validation date
      const validationQuery = query(
        validationHistoryRef,
        orderBy("validatedAt", "desc")
      );
      const snapshot = await getDocs(validationQuery);

      if (snapshot.empty) {
        return [];
      }

      // Extract validation history entries
      const validationHistory = [];
      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        validationHistory.push({
          id: docSnapshot.id,
          ...data,
          validatedAt: data.validatedAt?.toDate() || new Date()
        });
      });

      return validationHistory;
    } catch (error) {
      console.error("Error retrieving field validation history:", error);
      return [];
    }
  },

  async getValidationCountForRecord({ getters }, { dataSetId, recordId }) {
    if (!dataSetId || !recordId) {
      console.error("DataSetId and recordId are required");
      return 0;
    }
    try {
      // Reference to the validation history subcollection for this record
      const programRef = doc(db(), "programs", getters.programId);
      const validationHistoryRef = collection(
        doc(
          collection(programRef, "presetDataSets"),
          dataSetId,
          "data",
          recordId
        ),
        "validationHistory"
      );

      // Use a count approach instead of fetching all documents
      const snapshot = await getDocs(validationHistoryRef);

      // Return the count of validation entries
      return snapshot.size;
    } catch (error) {
      console.error("Error getting validation count:", error);
      // Return 0 on error, to avoid blocking validation in case of errors
      return 0;
    }
  },

  /**
   * Optimized validation for fields with dynamic validation - validates a single value without loading entire data set
   * This method uses multiple targeted queries and never loads all records for performance
   * Used during form submission and stepper navigation for all field types with dynamic validation
   */
  async validateTextFieldValue(
    { getters },
    { dataSetId, value, maxClaims = null, fieldLabel = null }
  ) {
    if (!dataSetId || !value) {
      return {
        isValid: false,
        error: "DataSet ID and value are required",
        recordId: null,
        claimsCount: 0,
        claimsExceeded: false
      };
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const dataSetRef = doc(
        collection(programRef, "presetDataSets"),
        dataSetId
      );
      const dataRef = collection(dataSetRef, "data");
      const trimmedValue = value.trim();

      // Strategy 1: Try exact case-sensitive match first
      let querySnapshot = await getDocs(
        query(dataRef, where("identifier", "==", trimmedValue))
      );

      // Strategy 2: If no exact match, try with different case variations
      if (querySnapshot.empty) {
        const caseVariations = [
          trimmedValue.toLowerCase(),
          trimmedValue.toUpperCase(),
          // Title case (first letter uppercase)
          trimmedValue.charAt(0).toUpperCase() +
            trimmedValue.slice(1).toLowerCase()
        ];

        for (const variation of caseVariations) {
          if (variation !== trimmedValue) {
            // Skip if same as original
            querySnapshot = await getDocs(
              query(dataRef, where("identifier", "==", variation))
            );
            if (!querySnapshot.empty) break;
          }
        }
      }

      // Strategy 3: If still no match, try using array-contains if identifier might be an array
      if (querySnapshot.empty) {
        try {
          querySnapshot = await getDocs(
            query(dataRef, where("identifier", "array-contains", trimmedValue))
          );
        } catch (arrayError) {
          // Ignore array-contains errors (field might not be an array)
        }
      }

      // Strategy 4: Final fallback - limited search with better performance
      if (querySnapshot.empty) {
        // Instead of loading ALL records, do a limited search on active records only
        const limitedQuery = query(
          dataRef,
          where("status", "==", "Active"),
          limit(500) // Reasonable limit to prevent performance issues
        );

        try {
          const limitedSnapshot = await getDocs(limitedQuery);

          let matchedDoc = null;
          limitedSnapshot.forEach(doc => {
            const data = doc.data();
            if (
              data.identifier &&
              data.identifier.toLowerCase() === trimmedValue.toLowerCase()
            ) {
              matchedDoc = { id: doc.id, ...data };
            }
          });

          if (matchedDoc) {
            // Create a mock querySnapshot for consistency
            querySnapshot = {
              empty: false,
              docs: [{ id: matchedDoc.id, data: () => matchedDoc }]
            };
          }
        } catch (limitedError) {
          console.warn("Limited search failed:", limitedError);
        }
      }

      // If still no match found, return invalid
      if (querySnapshot.empty) {
        const fieldName = fieldLabel || "Value";
        return {
          isValid: false,
          error: `${fieldName} is not valid`,
          recordId: null,
          claimsCount: 0,
          claimsExceeded: false
        };
      }

      // Found a match - get the first (should be only) result
      const matchDoc = querySnapshot.docs[0];
      const recordData = matchDoc.data();
      const recordId = matchDoc.id;

      // Check if record is active (if status field exists)
      if (recordData.status && recordData.status !== "Active") {
        const fieldName = fieldLabel || "Value";
        return {
          isValid: false,
          error: `${fieldName} is not valid`,
          recordId: recordId,
          claimsCount: 0,
          claimsExceeded: false
        };
      }

      // Check claims limit - priority: record level > dataset level > field level (maxClaims parameter)
      let claimsCount = 0;
      let claimsExceeded = false;
      let actualClaimsLimit = recordData.claimsLimit; // First check record level

      // If no record-level claims limit, check dataset level
      if (!actualClaimsLimit) {
        try {
          const dataSetDoc = await getDoc(dataSetRef);
          if (dataSetDoc.exists()) {
            const dataSetData = dataSetDoc.data();
            actualClaimsLimit = dataSetData.claimsLimit || maxClaims;
          } else {
            actualClaimsLimit = maxClaims;
          }
        } catch (error) {
          console.warn("Could not fetch dataset claims limit:", error);
          actualClaimsLimit = maxClaims;
        }
      }

      if (actualClaimsLimit !== null && actualClaimsLimit > 0) {
        // Get validation history count for this record
        const validationHistoryRef = collection(
          doc(dataSetRef, "data", recordId),
          "validationHistory"
        );

        const validationSnapshot = await getDocs(validationHistoryRef);
        claimsCount = validationSnapshot.size;
        claimsExceeded = claimsCount >= actualClaimsLimit;
      }

      return {
        isValid: true,
        error: null,
        recordId: recordId,
        recordData: recordData,
        claimsCount: claimsCount,
        claimsExceeded: claimsExceeded,
        claimsLimit: actualClaimsLimit
      };
    } catch (error) {
      console.error("Error validating text field value:", error);
      return {
        isValid: false,
        error: `Validation error: ${error.message}`,
        recordId: null,
        claimsCount: 0,
        claimsExceeded: false
      };
    }
  },

  setLoadingPresetRecord({ commit }, payload) {
    commit("setLoadingPresetRecord", payload);
  },

  setLoadingPresetRecords({ commit }, payload) {
    commit("setLoadingPresetRecords", payload);
  },

  resetPresetRecords({ commit }) {
    commit("setPresetRecords", []);
  }
};

const mutations = {
  setPresetRecords(state, payload) {
    state.presetRecords = Array.isArray(payload) ? payload : [];
  },

  setLoadingPresetRecord(state, payload) {
    state.loadingPresetRecord = Boolean(payload);
  },

  setLoadingPresetRecords(state, payload) {
    state.loadingPresetRecords = Boolean(payload);
  }
};

const getters = {
  presetRecords(state) {
    return state.presetRecords;
  },

  loadingPresetRecord(state) {
    return state.loadingPresetRecord;
  },

  loadingPresetRecords(state) {
    return state.loadingPresetRecords;
  },

  // Enhanced getters for better data access
  getPresetRecordById: state => id => {
    return state.presetRecords.find(record => record.id === id) || null;
  },

  getPresetRecordByIdentifier: state => identifier => {
    return (
      state.presetRecords.find(
        record => record.identifier?.toLowerCase() === identifier.toLowerCase()
      ) || null
    );
  },

  presetRecordsCount(state) {
    return state.presetRecords.length;
  },

  presetRecordsOptions(state) {
    return state.presetRecords.map(record => ({
      text: record.identifier || record.name || record.id,
      value: record.id
    }));
  },

  hasPresetRecords(state) {
    return state.presetRecords.length > 0;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
